<template>
  <v-alert
    :type="toast.type"
    variant="flat"
    density="comfortable"
    :closable="toast.closable"
    class="toast-item"
    rounded="lg"
    :role="toast.type === 'error' ? 'alert' : 'status'"
    :aria-live="toast.type === 'error' ? 'assertive' : 'polite'"
    @click:close="handleClose"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
  >
    <template #prepend>
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        size="20"
        width="2"
        color="white"
      />
    </template>

    <span class="toast-item__message">{{ toast.message }}</span>

    <!-- The actions sit under the message, not in the alert's append column: a v-btn label does
         not wrap, so a long one there took the width and left the message one word per line. -->
    <div v-if="toast.actions.length > 0" class="toast-item__actions">
      <v-btn
        v-for="(action, index) in toast.actions"
        :key="index"
        variant="text"
        size="small"
        color="white"
        class="toast-item__action"
        @click="handleAction(action)"
      >
        {{ action.label }}
      </v-btn>
    </div>

    <div
      v-if="showProgress && toast.duration !== false"
      class="toast-item__progress"
      :style="{ transform: `scaleX(${progressWidth / 100})` }"
    />
  </v-alert>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToastStore } from '../../stores/useToastStore'
import type { Toast, ToastAction } from '../../types/toast.types'

interface Props {
  toast: Toast
  showProgress?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showProgress: true,
})

const emit = defineEmits<{
  close: [id: string]
}>()

const store = useToastStore()

const progressWidth = ref(100)
let progressInterval: ReturnType<typeof setInterval> | null = null
let dismissTimeout: ReturnType<typeof setTimeout> | null = null

const isLoading = computed(() => {
  return props.toast.duration === false && !props.toast.closable
})

function handleClose() {
  emit('close', props.toast.id)
}

async function handleAction(action: ToastAction) {
  try {
    await action.onClick()
  } finally {
    handleClose()
  }
}

/**
 * Paused by a pointer that **moves** over the toast, not by one it arrived under. A toast lands
 * where the button that raised it often sits — the editor's « Enregistrer » is top right, like the
 * toasts — so the pointer that just clicked is already there: pausing on that `mouseenter` kept the
 * toast up for as long as the pointer rested, over the very button the person wanted next. A
 * browser re-dispatches a resting pointer after a layout change with no movement, which is how that
 * case is told apart.
 */
function handleMouseMove(event: MouseEvent) {
  if (props.toast.isPaused || (event.movementX === 0 && event.movementY === 0)) return
  if (store.config.pauseOnHover && props.toast.duration !== false) {
    store.pause(props.toast.id)
    clearTimers()
  }
}

function handleMouseLeave() {
  if (store.config.pauseOnHover && props.toast.isPaused) {
    const remainingTime = props.toast.remainingTime
    store.resume(props.toast.id)
    startProgressAndDismiss(remainingTime)
  }
}

function clearTimers() {
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
  if (dismissTimeout) {
    clearTimeout(dismissTimeout)
    dismissTimeout = null
  }
}

function startProgressAndDismiss(remainingDuration?: number) {
  if (props.toast.duration === false) return

  const totalDuration = props.toast.duration as number
  const duration = remainingDuration ?? totalDuration
  const startTime = Date.now()
  const startProgress = (duration / totalDuration) * 100

  progressInterval = setInterval(() => {
    const elapsed = Date.now() - startTime
    const remaining = duration - elapsed
    progressWidth.value = Math.max(0, (remaining / totalDuration) * 100)

    if (remaining <= 0 && progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }
  }, 50)

  dismissTimeout = setTimeout(() => {
    clearTimers()
    emit('close', props.toast.id)
  }, duration)

  progressWidth.value = startProgress
}

onMounted(() => {
  startProgressAndDismiss()
})

onUnmounted(() => {
  clearTimers()
})
</script>

<style scoped lang="scss">
@use '@/styles/design-tokens' as *;

.toast-item {
  position: relative;
  min-width: 300px;
  max-width: 420px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  color: #fff !important;

  @include phone {
    min-width: auto;
    max-width: none;
  }

  &__message {
    display: block;
    font-weight: 500;
    overflow-wrap: break-word;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
    margin-right: -8px;
  }

  // An undo is a thumb's reach away on a phone, and as big as any other target there.
  &__action {
    @include phone {
      min-height: $touch-target;
    }
  }

  &__progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(0, 0, 0, 0.2);
    transform-origin: left;
    transition: transform 50ms linear;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-item__progress {
    display: none;
  }
}
</style>
